import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, Search, Check, X, RotateCcw, Users, Trophy, ArrowRight, Gift } from "lucide-react";
import { listUsers, setVerification } from "../services/admin";
import { getLiveTraders, adminSetLiveTraders } from "../services/settings";
import { Badge, EmptyState } from "../components/common/Card";
import Reveal from "../components/common/Reveal";

export default function Admin() {
  const [data, setData] = useState({ users: [], counts: {} });
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [liveBase, setLiveBase] = useState("");

  const load = () => listUsers().then(setData).catch(() => toast.error("Could not load users"));
  useEffect(() => {
    load();
    getLiveTraders().then((d) => setLiveBase(String(d.base))).catch(() => {});
  }, []);

  const saveLive = async () => {
    try { const d = await adminSetLiveTraders(parseInt(liveBase) || 0); setLiveBase(String(d.base)); toast.success("Live traders count updated"); }
    catch { toast.error("Could not update"); }
  };

  const act = async (id, status) => {
    try { await setVerification(id, status); toast.success(`Marked ${status}`); load(); }
    catch { toast.error("Action failed"); }
  };

  const rows = useMemo(() => {
    return data.users.filter((u) => {
      if (filter !== "all" && u.verification_status !== filter) return false;
      const s = q.toLowerCase();
      return !s || u.name?.toLowerCase().includes(s) || u.email?.toLowerCase().includes(s) || u.trader_id?.includes(s);
    });
  }, [data.users, q, filter]);

  const tiles = [
    ["Total", data.counts.total || 0, "all"],
    ["Verified", data.counts.verified || 0, "verified"],
    ["Pending", data.counts.pending || 0, "pending"],
    ["Rejected", data.counts.rejected || 0, "rejected"],
  ];

  return (
    <div className="mx-auto max-w-6xl">
      <Reveal>
        <div className="mb-2 flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-neutral-500"><ShieldCheck size={14} /> Admin</div>
        <h1 className="font-head text-2xl font-bold uppercase tracking-tight text-white md:text-3xl">Trader Console</h1>
      </Reveal>

      <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        {tiles.map(([l, v, f]) => (
          <button key={l} onClick={() => setFilter(f)} className={`surface rounded-2xl p-5 text-left transition-colors ${filter === f ? "border-white/25" : ""}`} data-testid={`admin-tile-${f}`}>
            <div className="font-head text-2xl font-bold tabular-nums text-white">{v}</div>
            <div className="mt-1 font-mono text-[10px] uppercase tracking-widest text-neutral-500">{l}</div>
          </button>
        ))}
      </div>

      <div className="mt-6 grid gap-4 md:grid-cols-3">
        <div className="surface flex flex-col justify-between rounded-2xl p-5" data-testid="admin-live-settings">
          <div>
            <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-neutral-500">
              <span className="relative flex h-2 w-2"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400/60" /><span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" /></span>
              Live traders (homepage)
            </div>
            <p className="mt-1 text-sm text-neutral-400">Base count shown to visitors. Live activity fluctuates around this number automatically.</p>
          </div>
          <div className="mt-4 flex items-center gap-2">
            <input type="number" value={liveBase} onChange={(e) => setLiveBase(e.target.value)} data-testid="admin-live-input"
              className="w-28 rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2.5 font-mono text-sm text-white outline-none focus:border-[#d4d8dc]/60" />
            <button onClick={saveLive} className="btn-silver rounded-lg px-5 py-2.5 font-mono text-[11px] font-bold uppercase tracking-widest" data-testid="admin-live-save">Save</button>
          </div>
        </div>

        <div className="surface flex flex-col justify-between rounded-2xl p-5" data-testid="admin-leaderboard-card">
          <div>
            <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-[#8fd6b4]">
              <Trophy size={14} /> Top 10 Leaderboard
            </div>
            <p className="mt-1 text-sm text-neutral-400">Manage ranks, profit figures, and verified trader stats. Central real-time sync.</p>
          </div>
          <div className="mt-4">
            <Link to="/leaderboard" className="btn-silver inline-flex items-center gap-2 rounded-lg px-5 py-2.5 font-mono text-[11px] font-bold uppercase tracking-widest" data-testid="admin-manage-leaderboard-btn">
              Manage Leaderboard <ArrowRight size={13} />
            </Link>
          </div>
        </div>

        <div className="surface flex flex-col justify-between rounded-2xl p-5" data-testid="admin-giveaway-card">
          <div>
            <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-amber-300">
              <Gift size={14} /> Daily Giveaway
            </div>
            <p className="mt-1 text-sm text-neutral-400">Configure reward pool, winner slots, rules, and showcase winners.</p>
          </div>
          <div className="mt-4">
            <Link to="/giveaway" className="btn-silver inline-flex items-center gap-2 rounded-lg px-5 py-2.5 font-mono text-[11px] font-bold uppercase tracking-widest" data-testid="admin-manage-giveaway-btn">
              Manage Giveaway <ArrowRight size={13} />
            </Link>
          </div>
        </div>
      </div>


      <div className="mt-6 flex items-center gap-3">
        <div className="surface flex flex-1 items-center gap-2 rounded-xl px-4 py-2.5">
          <Search size={15} className="text-neutral-500" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email or Trader ID" data-testid="admin-search"
            className="w-full bg-transparent font-mono text-sm text-white outline-none placeholder:text-neutral-600" />
        </div>
      </div>

      <div className="mt-5">
        {rows.length === 0 ? (
          <EmptyState icon={Users} title="No traders" desc="No users match this filter." />
        ) : (
          <div className="surface overflow-hidden rounded-2xl">
            <div className="hidden grid-cols-12 border-b border-white/8 px-5 py-3 font-mono text-[10px] uppercase tracking-widest text-neutral-500 md:grid">
              <div className="col-span-3">Trader</div><div className="col-span-2">Trader ID</div>
              <div className="col-span-2">Country</div><div className="col-span-2">Status</div><div className="col-span-3 text-right">Actions</div>
            </div>
            {rows.map((u) => (
              <div key={u.id} className="grid grid-cols-2 items-center gap-y-2 border-b border-white/5 px-5 py-4 md:grid-cols-12" data-testid="admin-user-row">
                <div className="col-span-2 md:col-span-3">
                  <div className="text-sm text-white">{u.name}</div>
                  <div className="font-mono text-[11px] text-neutral-500">{u.email}</div>
                </div>
                <div className="col-span-1 font-mono text-sm text-neutral-300 md:col-span-2">{u.trader_id}</div>
                <div className="col-span-1 font-mono text-xs text-neutral-400 md:col-span-2">{u.country || "—"}</div>
                <div className="col-span-1 md:col-span-2">
                  <Badge tone={u.verification_status === "verified" ? "verified" : u.verification_status === "rejected" ? "rejected" : "pending"}>{u.verification_status}</Badge>
                </div>
                <div className="col-span-1 flex justify-end gap-2 md:col-span-3">
                  <button onClick={() => act(u.id, "verified")} title="Verify" className="grid h-8 w-8 place-items-center rounded-lg border border-emerald-400/25 text-emerald-300/90 hover:bg-emerald-400/10" data-testid="admin-verify-btn"><Check size={14} /></button>
                  <button onClick={() => act(u.id, "rejected")} title="Reject" className="grid h-8 w-8 place-items-center rounded-lg border border-rose-400/25 text-rose-300/90 hover:bg-rose-400/10" data-testid="admin-reject-btn"><X size={14} /></button>
                  <button onClick={() => act(u.id, "pending")} title="Reset" className="grid h-8 w-8 place-items-center rounded-lg border border-white/10 text-neutral-400 hover:text-white" data-testid="admin-reset-btn"><RotateCcw size={13} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
